import axios from 'axios';
import { MutableRefObject, useEffect, useRef } from 'react';
import { ECurrency } from 'Types/globals';
import cacheData from 'memory-cache';

export const useInterval = (callback: Function, delay) => {
  const savedCallback: MutableRefObject<Function> = useRef<Function>();

  // save callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    const tick = () => {
      savedCallback.current();
    };
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
};

export const debounce = (fn, time) => {
  let timeoutId;

  const wrapper = (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      timeoutId = null;
      fn(...args);
    }, time);
  };
  wrapper.cancel = () => {
    clearTimeout(timeoutId);
  };

  return wrapper;
};

export const internalFetch = async (url, body?) => {
  return axios.get(`http://localhost:${process.env.APPLICATION_PORT}${url}`, {
    withCredentials: true,
    data: body,
  });
};

export const fetchWithCache = async (url: string, options: any, minutes: number = 60) => {
  const value = cacheData.get(url);

  if (value && process.env.NODE_ENV === 'production') {
    return value;
  } else {
    const { data } = await internalFetch(url, options);

    cacheData.put(url, data, minutes * 1000 * 60);
    return data;
  }
};

export const transformForKey = input => {
  return input?.toString().toLowerCase().replace(/\s/g, '.');
};

export const isServer = () => {
  return typeof window === 'undefined';
};

export const amountToCurrency = (amount, currency_code, { forceTwoDecimals } = { forceTwoDecimals: false }) => {
  const currency = ECurrency?.[currency_code] ?? currency_code ?? '€';

  const raw = typeof amount === 'number' ? amount : Number(String(amount).replace(/[^0-9.]/g, ''));
  const safeAmount = Number.isNaN(raw) ? 0 : raw;

  let formatted;

  if (forceTwoDecimals) {
    const formatter = new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    });
    formatted = formatter.format(safeAmount).replace(',', '.');
  } else {
    const formatter = new Intl.NumberFormat('fr-FR');
    formatted = formatter.format(safeAmount).replace(',', '.');
  }

  return `${currency}${formatted}`;
};

export const currencyToAmount = amount => {
  const number = amount?.toString?.().replace(/[^0-9.]/g, '');
  let result = number?.toString().match(/([0-9]+\.?[0-9]{0,2})/);

  if (parseFloat(result?.[0]) == 0) {
    result[0] = 0;
  }

  return result?.[0]?.toString?.() || '';
};

export const cloneObject = (obj): any => {
  var clone = {};
  for (var i in obj) {
    if (typeof obj[i] == 'object' && obj[i] != null) clone[i] = cloneObject(obj[i]);
    else clone[i] = obj[i];
  }
  return clone;
};

export const valueToBoolean = (value: unknown, defaultValue = false): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();

    if (['true', '1'].includes(normalized)) return true;
    if (['false', '0'].includes(normalized)) return false;

    return defaultValue;
  }

  return defaultValue;
};

export const getTransactionSign = (type: string, status?: string) => {
  const withoutSign = ['canceled', 'rollback']?.includes(status ?? '');
  if (withoutSign) return `\xa0\xa0`;
  return [
    'refill',
    'win',
    'cashback',
    'bonus_to_real',
    'refund',
    'money_back',
    'bonus',
    'sport_win',
    'sport_refund',
  ]?.includes(type)
    ? '+'
    : '-';
};

export const seeItems = <T>(items: T[], showAll: boolean, maxItems: number): T[] =>
  showAll ? items : items?.slice(0, maxItems);
// FIXME: for beta backwards compatibility
export const cleanBody = body => {
  if (!body) return '';
  const node = document.createRange().createContextualFragment(body).firstChild;
  if (typeof node === 'object') {
    return node?.textContent;
  }

  return node;
};
